// Angular
import { Observable, Subject, filter, map, takeUntil } from 'rxjs';
import { Component, OnInit, HostListener, ChangeDetectorRef, ViewChild, ViewContainerRef, OnDestroy, HostBinding } from '@angular/core';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { Title } from '@angular/platform-browser';

// Libraries

import { Select, Store } from '@ngxs/store';

// Language list
import { locale as enLang } from './shared/languages/en';

// Models
import { AppCleanup, UserModel } from './shared/models';
import { DrawerModel } from './shared/models';
import { JobCommentDefaultGroupLocalStorage } from './modules/comment/models/job-comment-default-group-dto';

// Services
import { JobService } from './modules/job/services/job.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { GlobalService } from './shared/services';
import { SpinnerService } from './shared/components/spinner/services/spinner.service';
import { ErrorHandlerService } from './shared/services';
import { CoreService } from './shared/services';
import { AuthService } from './modules/auth';
import { SplashScreenService } from './shared/components/splash-screen/services/splash-screen.service';
import { CustomIconService } from './shared/services/custom-icon.service';
import { TranslationService } from './shared/services/translation.service';
import { LayoutConfigService } from './shared/services/layout-config.service';

// Store
import { AuthState } from './modules/auth/state/auth.state';
import { UserAuthState } from './modules/auth/state/user-auth.state';
import { UserAuthActions } from './modules/auth/state/user-auth.action';
import { StorageState } from './shared/state/storage.state';
import { StorageActions } from './shared/state/storage.action';
import { AuthActions } from './modules/auth/state/auth.action';
import { CompanyActions } from './modules/company/state/company.action';
import { LookupActions } from './shared/state/lookup.action';

// Enums
import { StatusType } from './shared/enums';
import { SpinnerSizeType } from './shared/components/spinner/spinner.component';

// Helpers
import { AssetHelper, UtilityHelper } from './shared/helpers';

// Hub
import { JobHub } from './modules/job/services/job.hub';
import { DrawerModeType } from './shared/enums/drawer-mode.enum';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends AppCleanup implements OnInit, OnDestroy {
  @HostBinding('class') classes = 'component-container';
  @Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
  @Select(UserAuthState.userLoaded) userLoaded$: Observable<boolean>;
  @Select(UserAuthState.currentUser) currentUser$: Observable<UserModel>;
  @Select(StorageState.tenantId) tenantId$: Observable<number>;
  @Select(StorageState.commentDrawerSettings) commentDrawerSettings$: Observable<string>;
  @Select(StorageState.modelDrawerSettings) modelDrawerSettings$: Observable<string>;
  @Select(StorageState.commentGroups) commentGroups$: Observable<JobCommentDefaultGroupLocalStorage[]>;
  @Select(StorageState.selectedFilterJobTypeIds) selectedFilterJobTypeIds$: Observable<number[]>;
  @Select(StorageState.darkMode) darkMode$: Observable<boolean>;
  darkMode: boolean;
  @Select(StorageState.brightness) brightness$: Observable<string>;
  brightness: string;
  @Select(StorageState.currentRoute) currentRoute$: Observable<string>;
  tenantId: number;
  title = 'Portal';
  showSplashScreen: boolean = false;
  userActivity;
  showAppSpinner = false;
  isMobile: boolean;
  favicon: HTMLLinkElement;
  broadcast = new BroadcastChannel('ngxs-sync');
  readonly SpinnerSizeType = SpinnerSizeType;

  @HostListener('window:scroll', ['$event']) // for window scroll events
  onScroll() {
    clearTimeout(this.userActivity);
    this.layoutConfigService.setMobileAwake();
    this.setTimeout();
  }

  constructor(
    private translationService: TranslationService,
    private router: Router,
    private layoutConfigService: LayoutConfigService,
    private splashScreenService: SplashScreenService,
    private jobService: JobService,
    private changeDetectorRef: ChangeDetectorRef,
    private deviceDetectorService: DeviceDetectorService,
    private globalService: GlobalService,
    private titleService: Title,
    private spinnerService: SpinnerService,
    private coreService: CoreService,
    private jobHub: JobHub,
    private errorHandlerService: ErrorHandlerService,
    private store: Store,
    private authService: AuthService,
    private customIconService: CustomIconService
  ) {
    super();
    this.customIconService.initilizeCustomIcons(); // Initilizing custom icons
    this.router.events
      .pipe(
        filter((event) => event instanceof RoutesRecognized),
        map((event) => {
          let data = null;
          let route = event['state'].root;

          while (route) {
            data = route.data || data;
            route = route.firstChild;
          }

          return data;
        })
      )
      .pipe(takeUntil(this.destroy))
      .subscribe((data) => {});

    this.setTimeout();

    this.router.events.pipe(takeUntil(this.destroy)).subscribe((value) => {
      if (value instanceof NavigationEnd) {
        this.store.dispatch(new StorageActions.SetCurrentRoute(value.urlAfterRedirects));
      }
    });
  }

  ngOnInit() {
    this.initializeLanguage(); // Regardless of login status
    this.initializeSplashScreen();

    if (this.isViewer) {
      this.jobHub.messageReceived$.pipe(takeUntil(this.destroy)).subscribe((data) => {
        if (data) {
          this.coreService.toast({ message: data, type: StatusType.WARNING });
        }
      });

      this.authService
        .getUserByHardcodedToken()
        .pipe(takeUntil(this.destroy))
        .subscribe({
          // If viewer logs user in
          next: (user) => {
            this.setUser(user);
            this.setAppData();
          },
          error: () => {},
        });
    } else {
      this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          let userLoadedSubject = new Subject<void>(); // This is a workaround for getUserByToken being triggered after logout
          this.userLoaded$.pipe(takeUntil(userLoadedSubject)).subscribe((userLoaded) => {
            if (!userLoaded) {
              userLoadedSubject.next();
              userLoadedSubject.complete();
              this.authService
                .getUserByToken()
                .pipe(takeUntil(this.destroy))
                .subscribe({
                  next: (user) => {
                    this.setUser(user);
                    this.setAppData();
                  },
                  error: () => {},
                });
            }
          });
        } else {
          this.layoutConfigService.setCurrentConfigToDefault();
        }
      });
    }

    this.tenantId$.pipe(takeUntil(this.destroy)).subscribe((tenantId) => {
      if (tenantId) {
        // Notify other tabs about the tenantId change
        this.broadcast.postMessage({ key: 'tenantId', value: tenantId });

        if (this.tenantId) {
          UtilityHelper.refreshApp();
        } else {
          this.tenantId = tenantId;
          this.configureTenantAppAssets(this.tenantId); // Configuring app assets after user logs in (based on tenant id or default configs)
        }
      } else {
        this.tenantId = null;
      }
    });

    this.globalService.isMobile.next(this.deviceDetectorService.isMobile()); // Setting isMobile Behavior Subject for the rest of the app

    this.getLocalStorage();

    this.spinnerService.showAppSpinner$.pipe(takeUntil(this.destroy)).subscribe((showAppSpinner) => {
      this.showAppSpinner = showAppSpinner;
      this.changeDetectorRef.detectChanges(); // Fixes NG0100 warning
    });

    const isOnIOS = UtilityHelper.isIos(); // Checking if iOS
    var eventName = isOnIOS ? 'pagehide' : 'beforeunload';
    // Moving host listener functionality here
    // Runs on page refresh
    window.addEventListener(eventName, (event) => {
      this.saveLayouts();
      this.saveCommentSelector();
    });

    // Listen for tenantId changes from other tabs
    this.broadcast.onmessage = (event) => {
      const { key, value } = event.data;
      if (key === 'tenantId') {
        // Update the tenantId property in StorageState
        this.store.dispatch(new StorageActions.SetTenantId(value));
      } else if (key === 'darkMode') {
        // Update the darkMode property in StorageState
        this.store.dispatch(new StorageActions.SetDarkMode(value));
      } else if (key === 'brightness') {
        // Update the brightness property in StorageState
        this.store.dispatch(new StorageActions.SetAppBrightness(value));
      }
    };
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.broadcast.close();
  }

  setUser(user: any) {
    this.store.dispatch(new AuthActions.SetToken(user.accessToken));
    this.store.dispatch(new UserAuthActions.SetUser(user));
  }

  setAppData() {
    this.store.dispatch(new CompanyActions.GetAll({ showSpinner: false, showToast: false }));
    this.store.dispatch(new LookupActions.GetAll({ showSpinner: false, showToast: false }));
  }

  initializeApp() {
    // this.initializeSplashScreen();
  }

  initializeLanguage() {
    this.translationService.loadTranslations(enLang); // register translations
    this.translationService
      .getSelectedLanguage()
      .pipe(takeUntil(this.destroy))
      .subscribe((language) => {
        this.translationService.setLanguage(language);
      });
  }

  initializeSplashScreen() {
    this.showSplashScreen = true;
    this.splashScreenService.initialized.pipe(takeUntil(this.destroy)).subscribe((initialized) => {
      if (initialized) {
        setTimeout(() => {
          this.showSplashScreen = false;
        }, 1300);
      }
    });
  }

  // TODO - Flag for removal, not in use... confirm w/ Isaac.
  configureInitialAppAssets() {
    this.setFaviconByDomain(); // Setting favicon to correct href based on domain
    this.setPageTitleByDomain();
    this.setNamesByDomain();
    this.layoutConfigService.setCurrentConfigByDomain();
    this.showSplashScreen = this.layoutConfigService.getCurrentConfig('loader.enabled'); // Setting loader to correct href based on domain
  }

  configureTenantAppAssets(tenantId: number) {
    this.setFaviconByTenant(tenantId); // Setting favicon based on token, in case of localhost (can't set with domain)
    this.setPageTitleByTenant(tenantId);
    this.setNamesByTenant(tenantId);
    this.layoutConfigService.setCurrentConfigByTenantId(this.tenantId);
    this.showSplashScreen = this.layoutConfigService.getCurrentConfig('loader.enabled'); // Setting loader to correct href based on tenant id
  }

  // TODO consider renaming...
  setTimeout() {
    this.userActivity = setTimeout(() => {
      this.layoutConfigService.setMobileSleep();
    }, 1000);
  }

  getLocalStorage() {
    this.commentGroups$.pipe(takeUntil(this.destroy)).subscribe((commentGroups) => {
      if (commentGroups) {
        this.jobService.jobCommentDefaultGroupsForLocalStorage = commentGroups;
      }
    });

    this.commentDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((commentDrawerSettings) => {
      if (!commentDrawerSettings) {
        let newCommentDrawerSettings = new DrawerModel();
        newCommentDrawerSettings.mode = DrawerModeType.OVER;
        newCommentDrawerSettings.opened = false;
        newCommentDrawerSettings.pinned = false;
        this.store.dispatch(new StorageActions.SetCommentDrawerSettings(newCommentDrawerSettings));
        this.changeDetectorRef.markForCheck();
      }
    });

    this.modelDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((modelDrawerSettings) => {
      if (!modelDrawerSettings) {
        let newModelDrawerSettings = new DrawerModel();
        newModelDrawerSettings.mode = DrawerModeType.OVER;
        newModelDrawerSettings.opened = false;
        newModelDrawerSettings.pinned = false;
        this.store.dispatch(new StorageActions.SetModelDrawerSettings(newModelDrawerSettings));
        this.changeDetectorRef.markForCheck();
      }
    });

    this.selectedFilterJobTypeIds$.pipe(takeUntil(this.destroy)).subscribe((selectedFilterJobTypeIds) => {
      if (!selectedFilterJobTypeIds) {
        this.store.dispatch(new StorageActions.SetSelectedFilterJobTypeIds([]));
      }
    });

    this.currentRoute$.pipe(takeUntil(this.destroy)).subscribe((currentRoute) => {
      if (currentRoute) {
        if (currentRoute.includes('auth')) {
          this.darkMode = false;
          this.brightness = '100';
          document.body.classList.remove('dark-mode');
          document.body.style.opacity = '100%';
        } else {
          this.darkMode$.pipe(takeUntil(this.destroy)).subscribe((darkMode) => {
            if (darkMode === undefined) {
              this.store.dispatch(new StorageActions.SetDarkMode(false));
            }

            if (this.darkMode !== darkMode) {
              this.darkMode = darkMode;

              // Notify other tabs about the darkMode change
              this.broadcast.postMessage({ key: 'darkMode', value: darkMode });

              if (darkMode) {
                document.body.classList.add('dark-mode');
              } else {
                document.body.classList.remove('dark-mode');
              }
            }
          });

          this.brightness$.pipe(takeUntil(this.destroy)).subscribe((brightness) => {
            if (brightness === undefined) {
              this.store.dispatch(new StorageActions.SetAppBrightness('100'));
            }

            if (this.brightness !== brightness) {
              // Notify other tabs about the brightness change
              this.broadcast.postMessage({ key: 'brightness', value: brightness });

              this.brightness = brightness;
              document.body.style.opacity = `${brightness}%`;
            }
          });
        }
      }
    });
  }

  saveLayouts() {
    // Runs on page refresh
    // let panelsModel: PanelsModel = new PanelsModel();
    // panelsModel.drawers.comment = new DrawerModel();
    // panelsModel.drawers.model = new DrawerModel();
    // panelsModel.drawers.comment = {
    // 	opened: this.jobService.commentDrawer?.opened,
    // 	mode: this.jobService.commentDrawerMode,
    // };
    // panelsModel.drawers.model = {
    // 	opened: this.jobService.commentDrawer?.opened,
    // 	mode: this.jobService.commentDrawerMode,
    // };
    // panelsModel.jobTypeIds = this.jobService.jobTypeIdsToShow;
    // this.store.dispatch(new StorageActions.SetPanels(panelsModel));
  }

  saveCommentSelector() {
    this.jobService.saveLocalStorageSettings();
  }

  setFaviconByDomain() {
    // Setting favicon variable.
    this.favicon = document.querySelector('#favicon');
    if (this.favicon) {
      this.favicon.href = new AssetHelper().getFaviconByDomain();
    }
  }

  setFaviconByTenant(tenantId: number) {
    // Setting favicon variable
    this.favicon = document.querySelector('#favicon');
    if (this.favicon) {
      this.favicon.href = new AssetHelper().getFaviconByTenant(tenantId);
    }
  }

  setPageTitleByDomain() {
    this.titleService.setTitle(new AssetHelper().getTitleByDomain());
  }

  setPageTitleByTenant(tenantId: number) {
    this.titleService.setTitle(new AssetHelper().getTitleByTenant(tenantId));
  }

  setNamesByDomain() {
    this.globalService.tenantNameShort.next(new AssetHelper().getShortNameByDomain());
    this.globalService.tenantNameFull.next(new AssetHelper().getFullNameByDomain());
  }

  setNamesByTenant(tenantId: number) {
    this.globalService.tenantNameShort.next(new AssetHelper().getShortNameByTenant(tenantId));
    this.globalService.tenantNameFull.next(new AssetHelper().getFullNameByTenant(tenantId));
  }

  // TODO - Flag for removal, not in use... confirm w/ Isaac.
  throwServerError() {
    this.errorHandlerService.throwServerError();
  }

  // TODO - Flag for removal, not in use... confirm w/ Isaac.
  throwClientError() {
    this.errorHandlerService.throwClientError();
  }
}
